import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail, isNetworkError } from "../../lib/api";
import { deptLabel } from "../../lib/labels";
import { toast } from "sonner";
import { Check, X, ChevronLeft, ChevronRight, Inbox } from "lucide-react";
import PageHeader from "../../components/system/PageHeader";
import Card from "../../components/system/Card";
import Button from "../../components/system/Button";
import StatusChip from "../../components/system/StatusChip";
import EmptyState from "../../components/system/EmptyState";
import Input, { Select } from "../../components/system/Input";
import OfflineUnavailable from "../../components/system/OfflineUnavailable";
import { useAuth } from "../../context/AuthContext";

const RECENT_DAYS = 7;
const recentCutoffMs = () => Date.now() - RECENT_DAYS * 24 * 60 * 60 * 1000;

const STATUS_TONE = { pending: "neutral", approved: "fresh", rejected: "expired" };

export default function RequestsAdminPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [depts, setDepts] = useState([]);
  const [status, setStatus] = useState("pending");
  const [view, setView] = useState("recent"); // "recent" | "history"
  const [target, setTarget] = useState(null);
  const [decision, setDecision] = useState({ name: "", category: "", department_id: "", note: "" });
  const [submitting, setSubmitting] = useState(false);
  // Lightbox state for photo enlargement. { reqId, index } when open.
  const [lightbox, setLightbox] = useState(null);
  const [unavailableOffline, setUnavailableOffline] = useState(false);

  const load = async () => {
    try {
      const [r, d] = await Promise.all([
        api.get("/product-requests", { params: status !== "all" ? { status } : {} }),
        api.get("/departments"),
      ]);
      setItems(r.data); setDepts(d.data);
      setUnavailableOffline(false);
    } catch (e) {
      // Offline & no cache → render OfflineUnavailable instead of an
      // empty "no requests" state (which would imply submissions were
      // lost). Suppress the red Network Error toast in this state.
      if (isNetworkError(e)) {
        setItems([]);
        setUnavailableOffline(true);
      } else if (e?.response) {
        toast.error(formatApiErrorDetail(e));
      }
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [status]);

  const openApprove = (r) => {
    setTarget({ mode: "approve", req: r });
    setDecision({ name: r.name_guess || "", category: "", department_id: r.department_id || depts[0]?.id || "", note: "" });
  };
  const openReject = (r) => {
    setTarget({ mode: "reject", req: r });
    setDecision({ name: "", category: "", department_id: "", note: "" });
  };

  const submit = async () => {
    if (!target) return;
    setSubmitting(true);
    try {
      const path = target.mode === "approve" ? "approve" : "reject";
      await api.post(`/product-requests/${target.req.id}/${path}`, decision);
      toast.success(target.mode === "approve" ? "Approved" : "Rejected");
      setTarget(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e)); } finally { setSubmitting(false); }
  };

  const { recent, history, recentCount, historyCount } = useMemo(() => {
    const cutoff = recentCutoffMs();
    const r = [], h = [];
    for (const it of items) {
      const t = new Date(it.created_at).getTime();
      (Number.isFinite(t) && t >= cutoff ? r : h).push(it);
    }
    return { recent: r, history: h, recentCount: r.length, historyCount: h.length };
  }, [items]);

  const visible = view === "recent" ? recent : history;

  return (
    <div data-testid="admin-requests-page">
      <PageHeader
        eyebrow="Awaiting your decision"
        title="Product Requests"
      />

      {unavailableOffline && (
        <OfflineUnavailable
          title="Product requests unavailable offline"
          description="This page needs a network connection. Reconnect to refresh — pending submissions are safe on the server."
          onRetry={load}
          data-testid="admin-requests-offline-unavailable"
        />
      )}

      {!unavailableOffline && (
        <>
      {/* Recent / History tabs */}
      <div className="flex gap-2 mb-3" data-testid="req-view-tabs">
        {[
          { k: "recent",  l: `Recent (${RECENT_DAYS}d)`, n: recentCount },
          { k: "history", l: "History",                  n: historyCount },
        ].map(({ k, l, n }) => (
          <button
            key={k}
            onClick={() => setView(k)}
            className={[
              "h-10 px-4 rounded-button font-display text-xs uppercase tracking-wide inline-flex items-center gap-2 transition-all duration-150 active:scale-[0.98]",
              view === k
                ? "bg-ink text-white shadow-rest"
                : "bg-paper-2 text-ink-2 shadow-rest hover:shadow-raised",
            ].join(" ")}
            data-testid={`req-view-${k}`}
          >
            {l}
            <span className={`font-mono text-[10px] ${view === k ? "text-white/70" : "text-ink-3"}`}>{n}</span>
          </button>
        ))}
      </div>

      {/* Status filter — secondary */}
      <div className="flex gap-2 mb-4 overflow-x-auto no-scrollbar -mx-6 px-6 sm:mx-0 sm:px-0">
        {["pending", "approved", "rejected", "all"].map((s) => (
          <button
            key={s}
            onClick={() => setStatus(s)}
            className={[
              "shrink-0 h-9 px-3 rounded-chip font-mono text-[11px] font-bold uppercase tracking-wider transition-all duration-150 active:scale-[0.98]",
              status === s
                ? "bg-paper-sunk text-ink shadow-press"
                : "bg-paper-2 text-ink-2 shadow-rest hover:shadow-raised",
            ].join(" ")}
            data-testid={`req-filter-${s}`}
          >
            {s}
          </button>
        ))}
      </div>

      {visible.length === 0 ? (
        <EmptyState
          icon={<Inbox size={40} strokeWidth={1.5} />}
          title={view === "recent" ? "No recent requests" : "No older requests"}
          description={
            view === "recent"
              ? `Nothing has been submitted in the last ${RECENT_DAYS} days.`
              : "History is empty for this filter."
          }
          data-testid="req-empty"
        />
      ) : (
        <Card variant="raised" className="overflow-hidden">
          {visible.map((r, idx) => (
            <div
              key={r.id}
              className={`p-4 ${idx > 0 ? "border-t border-border/40" : ""}`}
              data-testid={`admin-request-row-${r.id}`}
            >
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="font-display font-semibold text-lg">{r.name_guess || "(unnamed)"}</div>
                  <div className="data-sku break-words">{r.barcode} · by {r.submitted_by_email}</div>
                  {r.brand && <div className="text-xs mt-1 break-words"><span className="font-mono uppercase tracking-wider text-ink-3">Brand:</span> {r.brand}</div>}
                  {r.notes && <div className="text-sm mt-1 break-words">{r.notes}</div>}
                  {r.decision_note && <div className="text-xs text-muted-foreground mt-1 break-words">Note: {r.decision_note}</div>}
                  <div className="font-mono text-[10px] text-ink-3 mt-1 flex items-center gap-2 flex-wrap">
                    <span>{new Date(r.created_at).toLocaleString()}</span>
                    {r.extraction_source && r.extraction_source !== "manual" && (
                      <span className="bg-brand-tint text-brand px-1.5 py-0.5 rounded-chip uppercase tracking-wider" data-testid={`admin-request-source-${r.id}`}>
                        via {r.extraction_source.replace("_", " ")}
                      </span>
                    )}
                  </div>
                  {Array.isArray(r.photos) && r.photos.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2" data-testid={`admin-request-photos-${r.id}`}>
                      {r.photos.map((src, pidx) => (
                        <button
                          key={pidx}
                          type="button"
                          onClick={() => setLightbox({ reqId: r.id, photos: r.photos, index: pidx })}
                          className="relative w-16 h-16 rounded-button overflow-hidden bg-paper-sunk shadow-rest active:scale-95 transition-transform"
                          data-testid={`admin-request-photo-${r.id}-${pidx}`}
                          aria-label={`Open photo ${pidx + 1} of ${r.photos.length}`}
                        >
                          <img src={src} alt={`Item ${pidx + 1} for ${r.name_guess || r.barcode}`} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-wrap sm:shrink-0">
                  <StatusChip tone={STATUS_TONE[r.status] || "neutral"}>{r.status}</StatusChip>
                  {isAdmin && r.status === "pending" && (
                    <>
                      <Button tier="primary" size="sm" onClick={() => openApprove(r)} data-testid={`approve-${r.id}`}>
                        <Check size={14} /> Approve
                      </Button>
                      <Button tier="secondary" size="sm" onClick={() => openReject(r)} data-testid={`reject-${r.id}`}>
                        <X size={14} /> Reject
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </Card>
      )}
        </>
      )}

      {lightbox && (
        <PhotoLightbox
          photos={lightbox.photos}
          index={lightbox.index}
          onClose={() => setLightbox(null)}
          onIndexChange={(i) => setLightbox((cur) => (cur ? { ...cur, index: i } : cur))}
        />
      )}

      {target && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-ink/60 px-3">
          <Card variant="raised" className="w-full sm:max-w-md p-6" data-testid="decision-dialog">
            <div className="flex justify-between items-start mb-4">
              <div className="font-display font-black text-2xl">{target.mode === "approve" ? "Approve request" : "Reject request"}</div>
              <button onClick={() => setTarget(null)} className="p-2 -mt-2 -mr-2 rounded-button text-ink-2 hover:text-ink"><X size={20} /></button>
            </div>
            <div className="data-sku mb-3">Barcode {target.req.barcode}</div>
            {target.mode === "approve" ? (
              <div className="space-y-3">
                <Input placeholder="Product name" value={decision.name} onChange={(e) => setDecision({ ...decision, name: e.target.value })} data-testid="decision-name" />
                <Input placeholder="Category" value={decision.category} onChange={(e) => setDecision({ ...decision, category: e.target.value })} data-testid="decision-category" />
                <Select value={decision.department_id} onChange={(e) => setDecision({ ...decision, department_id: e.target.value })} data-testid="decision-department">
                  {depts.map((d) => <option key={d.id} value={d.id}>{deptLabel(d.name)}</option>)}
                </Select>
                <textarea placeholder="Note (optional)" value={decision.note} onChange={(e) => setDecision({ ...decision, note: e.target.value })} rows={2} className="w-full p-3 rounded-button bg-paper-2 border border-border/60 shadow-rest focus:shadow-raised focus:outline-none text-sm transition-shadow" data-testid="decision-note" />
              </div>
            ) : (
              <textarea placeholder="Rejection reason" value={decision.note} onChange={(e) => setDecision({ ...decision, note: e.target.value })} rows={3} className="w-full p-3 rounded-button bg-paper-2 border border-border/60 shadow-rest focus:shadow-raised focus:outline-none text-sm transition-shadow" data-testid="decision-note" />
            )}
            <Button tier="primary" className="w-full mt-4" onClick={submit} disabled={submitting} data-testid="submit-decision">
              {submitting ? "Saving…" : target.mode === "approve" ? "Approve & add to catalog" : "Reject"}
            </Button>
          </Card>
        </div>
      )}
    </div>
  );
}

function PhotoLightbox({ photos, index, onClose, onIndexChange }) {
  const total = photos.length;
  const safeIdx = ((index % total) + total) % total;
  const prev = () => onIndexChange((safeIdx - 1 + total) % total);
  const next = () => onIndexChange((safeIdx + 1) % total);

  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft" && total > 1) prev();
      else if (e.key === "ArrowRight" && total > 1) next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safeIdx, total]);

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="photo-lightbox"
    >
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        className="absolute top-4 right-4 w-11 h-11 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center backdrop-blur"
        aria-label="Close photo"
        data-testid="photo-lightbox-close"
      >
        <X size={22} strokeWidth={2.5} />
      </button>
      <div className="absolute top-4 left-4 font-mono text-[11px] uppercase tracking-wider text-white/80" data-testid="photo-lightbox-counter">
        {safeIdx + 1} / {total}
      </div>
      {total > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); prev(); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center backdrop-blur"
            aria-label="Previous photo"
            data-testid="photo-lightbox-prev"
          >
            <ChevronLeft size={24} strokeWidth={2.5} />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); next(); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center backdrop-blur"
            aria-label="Next photo"
            data-testid="photo-lightbox-next"
          >
            <ChevronRight size={24} strokeWidth={2.5} />
          </button>
        </>
      )}
      <img
        src={photos[safeIdx]}
        alt={`Item ${safeIdx + 1} of ${total}`}
        className="max-w-full max-h-full object-contain"
        onClick={(e) => e.stopPropagation()}
        data-testid="photo-lightbox-image"
      />
    </div>
  );
}
